import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Add, Cancel, Delete, Edit, Save, Block } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardActions, CardContent, CardHeader, Divider, FormControlLabel, Grid, Stack, Switch, TableContainer } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { GameCardContent } from '../../../features/game/components/GameCard/GameCard'
import { TrackedGameArchetype } from '../../../features/live-events/types/TrackedGame'
import { displaySnackBar } from '../../../features/snackbar'
import usePage from '../../../hooks/usePage'
import { useRequiredParams } from '../../../hooks/useRequiredParams'
import PageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { TranslationMap } from '../../../types/TranslationMap'
import {
  useGetTrackedGameQuery,
  useCreateLiveEventArchetypeMutation,
  useDeleteLiveEventArchetypeMutation,
  useUpdateLiveEventArchetypeMutation,
} from '../../api/internal'
import { useGetTrackedArchetypesQuery } from '../../api/markets'
import { ActionButton } from '../../components/ActionButton/ActionButton'
import { BackButton } from '../../components/BackButton/BackButton'
import FloatingToolbar from '../../components/FloatingToolbar/FloatingToolbar'
import { InputTranslator } from '../../components/InputTranslator/InputTranslator'
import { MarketLevelBlueprintSearch } from '../../components/MarketLevelBlueprintSearch/MarketLevelBlueprintSearch'
import { languageServiceNameResolver } from '../../components/MarketLevelBlueprintSearch/marketLevelBlueprintGrouping'
import { CustomerBlueprintLink } from '../../features/live-events-tracking/components/CustomerBlueprintLink/CustomerBlueprintLink'
import { acceptedEventTypeIds } from '../../features/live-events-tracking/helpers/acceptedEventTypes'
import { useGameBlueprintEventTypes } from '../../features/live-events-tracking/hooks/useGameBlueprintEventTypes'
import { InputTranslatorType } from '../../types/InputTranslatorType'

/**
 * Props for the EventArchetypesEditPage component
 */
type EventArchetypesEditPageProps = {
  children?: ReactNode
}

/**
 * Page component for editing live event archetypes for a tracked game.
 *
 * This component provides functionality to:
 * - Add new live event archetypes with multilingual names
 * - Edit existing archetypes
 * - Delete archetypes with confirmation
 * - Display a table of all archetypes for the game
 *
 * The component handles both creation mode (when no archetypeId is provided)
 * and edit mode (when archetypeId is provided in the URL params).
 *
 * @param props - Component props
 * @param props.children - Optional child components
 * @returns JSX element representing the event archetypes edit page
 */
export const EventArchetypesEditPage: FC<EventArchetypesEditPageProps> = ({ children }) => {
  usePage(PageService.getInternalPageWithId('internal-event-archetypes-edit'))
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { trackedGameId, archetypeId } = useRequiredParams<{ trackedGameId: string; archetypeId: string }>()
  const getTrackedGameQuery = useGetTrackedGameQuery(trackedGameId, { skip: !trackedGameId })
  const trackedGame = getTrackedGameQuery.data
  const { data: trackedArchetypes } = useGetTrackedArchetypesQuery()
  const [createLiveEventArchetype, { isLoading: isCreating }] = useCreateLiveEventArchetypeMutation()
  const [deleteLiveEventArchetype, { isLoading: isDeleting }] = useDeleteLiveEventArchetypeMutation()
  const [updateLiveEventArchetype, { isLoading: isUpdating }] = useUpdateLiveEventArchetypeMutation()
  const isLoading = isCreating || isDeleting || isUpdating

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<TrackedGameArchetype> | undefined>()

  /**
   * Default form values based on the current archetype being edited
   */
  const defaultValues = useMemo(() => {
    const archetype = trackedGame?.archetypes?.find((archetype) => archetype.archetypeId === archetypeId)
    return {
      names: archetype?.names || { en: '', ja: '', zh: '' },
      discontinued: archetype?.discontinued || false,
      descriptions: archetype?.descriptions || { en: '', ja: '', zh: '' },
      marketArchetypeId: archetype?.marketArchetypeId || undefined,
    }
  }, [archetypeId, trackedGame?.archetypes])

  const form = useForm<{ names: TranslationMap; discontinued: boolean; descriptions: TranslationMap; marketArchetypeId?: string }>({
    defaultValues: defaultValues,
  })

  const { control, getValues, reset, watch } = form

  // Watch the names field to validate English name requirement
  const watchedNames = watch('names')
  const watchedMarketArchetypeId = watch('marketArchetypeId')

  // Pointing a blueprint at a Market Level Blueprint that does not accept the types its own events
  // use is never blocked, so the types that would start reporting as mismatches are named here.
  const { typeIds: blueprintEventTypeIds, isLoading: blueprintEventTypesLoading } = useGameBlueprintEventTypes(trackedGameId, archetypeId)
  // Saving while those types are still loading would skip the warning, so it waits for them.
  const waitingForMismatchData = !!watchedMarketArchetypeId && blueprintEventTypesLoading
  const mismatchedTypeNames = useMemo(() => {
    const marketArchetype = trackedArchetypes?.find((candidate) => candidate.id === watchedMarketArchetypeId)
    const accepted = acceptedEventTypeIds(marketArchetype?.eventTypes)
    if (!marketArchetype || accepted.length === 0) return []
    return blueprintEventTypeIds.filter((typeId) => !accepted.includes(typeId)).map((typeId) => languageServiceNameResolver.eventTypeName(typeId) || typeId)
  }, [blueprintEventTypeIds, trackedArchetypes, watchedMarketArchetypeId])

  /**
   * Validates that the English name is provided
   */
  const isEnglishNameValid = useMemo(() => {
    return watchedNames?.en && watchedNames.en.trim().length > 0
  }, [watchedNames?.en])

  useEffect(() => {
    if (archetypeId) {
      reset(defaultValues)
    }
  }, [archetypeId, reset, defaultValues])

  /**
   * Handles the creation of a new live event archetype
   *
   * Validates that a tracked game ID is available, then creates the archetype
   * with the current form values. Resets the form on success.
   */
  const handleAddArchetype = async () => {
    const names = getValues('names')
    const discontinued = getValues('discontinued')
    const descriptions = getValues('descriptions')
    const marketArchetypeId = getValues('marketArchetypeId')

    if (!trackedGameId) {
      throw new Error('No tracked game ID available')
    }

    try {
      await createLiveEventArchetype({
        gameId: trackedGameId,
        names: names,
        discontinued: discontinued,
        descriptions: descriptions,
        marketArchetypeId: marketArchetypeId,
      }).unwrap()

      reset({ names: { en: '', ja: '', zh: '' }, discontinued: false, descriptions: { en: '', ja: '', zh: '' }, marketArchetypeId: undefined })
    } catch (error) {
      displaySnackBar({ message: t('internal-live-events:failed_to_create_live_event_archetype'), severity: 'error', open: true })
    }
  }

  /**
   * Initiates the deletion process for an archetype
   *
   * Opens a confirmation dialog with details about the archetype to be deleted
   *
   * @param row - The archetype row data
   * @param rowIndex - Index of the row in the table
   */
  const handleRemoveArchetype = useCallback(
    async (row: TrackedGameArchetype, rowIndex: number) => {
      setConfirmDialogData({
        title: t('internal-live-events:delete_archetype_confirm_dialog_title'),
        confirmText: t('internal-live-events:delete_archetype_confirm_dialog_confirm_text', { name: row.names.en }),
        actionText: t('internal-common:delete'),
        destructiveAction: true,
        data: row,
      })
      setConfirmDialogOpen(true)
    },
    [t]
  )

  /**
   * Executes the deletion of an archetype after confirmation
   *
   * @param confirmData - Confirmation dialog data containing the archetype to delete
   */
  const handleConfirmDelete = async (confirmData?: ConfirmDialogData<TrackedGameArchetype>) => {
    if (!confirmData?.data?.archetypeId) {
      displaySnackBar({ message: t('internal-live-events:cannot_delete_archetype_missing_id'), severity: 'error', open: true })
      return
    }

    try {
      await deleteLiveEventArchetype(confirmData.data.archetypeId).unwrap()
      displaySnackBar({ message: t('internal-live-events:archetype_deleted_successfully'), severity: 'success', open: true })
    } catch (error) {
      displaySnackBar({ message: t('internal-live-events:failed_to_delete_archetype'), severity: 'error', open: true })
    }
  }

  /**
   * Handles the closing of the confirmation dialog
   *
   * @param confirmData - Confirmation dialog data if user confirmed the action
   */
  const handleConfirmDialogClose = (confirmData?: ConfirmDialogData<TrackedGameArchetype>) => {
    setConfirmDialogOpen(false)
    if (confirmData) {
      handleConfirmDelete(confirmData)
    }
  }

  /**
   * Navigates to the edit page for a specific archetype
   *
   * @param row - The archetype row data
   * @param rowIndex - Index of the row in the table
   */
  const handleEditArchetype = useCallback(
    async (row: TrackedGameArchetype, rowIndex: number) => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      navigate(`/internal/live-events-tracking/events/${trackedGameId}/archetypes/edit/${row.archetypeId}`)
    },
    [navigate, trackedGameId]
  )

  /**
   * Helper function to get the market level blueprint name from the blueprint ID
   */
  const getMarketLevelBlueprintName = useCallback(
    (blueprintId?: string): string | undefined => {
      if (!blueprintId || !trackedArchetypes) {
        return undefined
      }

      const archetype = trackedArchetypes.find((arch) => arch.id === blueprintId)
      if (archetype) {
        return archetype.names.en || archetype.names.ja || archetype.names.zh || 'Unnamed'
      }

      return undefined
    },
    [trackedArchetypes]
  )

  /**
   * Custom props passed to the table component for action handlers
   */
  const customProps = useMemo(
    () => ({
      handleRemoveArchetype,
      handleEditArchetype,
      getMarketLevelBlueprintName,
    }),
    [handleRemoveArchetype, handleEditArchetype, getMarketLevelBlueprintName]
  )

  const archetypes: TrackedGameArchetype[] = trackedGame?.archetypes || []

  const tableColumns: GRTableColumn<TrackedGameArchetype, typeof customProps>[] = useMemo(
    () => [
      {
        labelAccessor: () => <Trans i18nKey="internal-common:name" />,
        accessor: ({ row }) => row.names.en,
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
        sortable: true,
        sortOrder: SortOrder.DESC,
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:description" />,
        accessor: ({ row }) => {
          const description = row.descriptions?.en || ''
          if (!description) return ''

          return (
            <GRTooltip content={description}>
              <Box
                sx={{
                  maxWidth: '300px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {description}
              </Box>
            </GRTooltip>
          )
        },
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
        sortable: true,
      },
      {
        labelAccessor: () => <Trans i18nKey="live-events:market_level_blueprint" />,
        accessor: ({ row, customTableProps }) => {
          const blueprintName = customTableProps?.getMarketLevelBlueprintName(row.marketArchetypeId)
          return blueprintName || ''
        },
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => customTableProps?.getMarketLevelBlueprintName(row.marketArchetypeId) || '',
      },
      {
        labelAccessor: () => <Trans i18nKey="live-events:live_event_evolution_type_discontinued" />,
        accessor: ({ row }) => {
          return row.discontinued ? <Block color="error" /> : undefined
        },
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 90 } },
        sortable: true,
        sortAccessor: ({ row }) => (row.discontinued ? 1 : 0),
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:actions" />,
        headerCellProps: { sx: { width: 160 } },
        accessor: ({ row, rowIndex, customTableProps }) => (
          <Stack direction="row" spacing={1} justifyContent="center">
            <CustomerBlueprintLink gameId={trackedGameId} archetypeId={row.archetypeId} />
            <ActionButton onClick={() => customTableProps?.handleEditArchetype(row, rowIndex)} disabled={isDeleting}>
              <Edit fontSize="small" />
            </ActionButton>
            <ActionButton color="warning" onClick={() => customTableProps?.handleRemoveArchetype(row, rowIndex)} disabled={isDeleting}>
              <Delete fontSize="small" />
            </ActionButton>
          </Stack>
        ),
      },
    ],
    [isDeleting, trackedGameId]
  )

  /**
   * Handles cancellation of the edit operation
   *
   * Resets the form and navigates back to the archetypes list
   */
  const handleCancel = () => {
    reset({ names: { en: '', ja: '', zh: '' }, discontinued: false, descriptions: { en: '', ja: '', zh: '' }, marketArchetypeId: undefined })
    navigate(`/internal/live-events-tracking/events/${trackedGameId}/archetypes/edit`)
  }

  /**
   * Handles saving changes to an existing archetype
   *
   * Validates that an archetype ID is available, then updates the archetype
   * with the current form values. Shows success/error messages and navigates
   * back to the archetypes list on success.
   */
  const handleSaveArchetype = async () => {
    const names = getValues('names')
    const discontinued = getValues('discontinued')
    const descriptions = getValues('descriptions')
    const marketArchetypeId = getValues('marketArchetypeId')

    if (!archetypeId) {
      displaySnackBar({ message: t('internal-live-events:cannot_save_archetype_missing_id'), severity: 'error', open: true })
      return
    }

    try {
      await updateLiveEventArchetype({
        id: archetypeId,
        names: names,
        discontinued: discontinued,
        descriptions: descriptions,
        marketArchetypeId: marketArchetypeId,
      }).unwrap()

      displaySnackBar({ message: t('internal-live-events:archetype_updated_successfully'), severity: 'success', open: true })
      reset({ names: { en: '', ja: '', zh: '' }, discontinued: false, descriptions: { en: '', ja: '', zh: '' }, marketArchetypeId: undefined })
      navigate(`/internal/live-events-tracking/events/${trackedGameId}/archetypes/edit`)
    } catch (error) {
      displaySnackBar({ message: t('internal-live-events:failed_to_update_archetype'), severity: 'error', open: true })
    }
  }

  const [columns, setColumns] = useState<GRTableColumn<TrackedGameArchetype, typeof customProps>[]>(tableColumns)

  /**
   * Handles updates to table columns configuration
   *
   * @param updatedColumns - The updated column configuration
   */
  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<TrackedGameArchetype, typeof customProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  return (
    <Box>
      <Box sx={{ marginBottom: 2 }}>{trackedGame && <GameCardContent game={trackedGame.game} disableGameLink />}</Box>
      <FormProvider {...form}>
        <Card>
          <CardHeader title={archetypeId ? t('internal-live-events:edit_live_event_archetype') : t('internal-live-events:add_new_live_event_archetype')} />
          <Divider />
          <CardContent>
            <Grid container spacing={2} flexWrap="wrap">
              <Grid xs={12} item>
                <Controller
                  name="names"
                  control={control}
                  render={({ field }) => (
                    <InputTranslator
                      label={t('internal-common:name')}
                      value={field.value}
                      inputType={InputTranslatorType.Text}
                      dialogTitle={t('internal-live-events:live_event_archetype_name')}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} item>
                <Controller
                  name="descriptions"
                  control={control}
                  render={({ field }) => (
                    <InputTranslator
                      label={t('internal-live-events:description')}
                      value={field.value}
                      inputType={InputTranslatorType.WYSIWYG}
                      dialogTitle={t('internal-live-events:description')}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>

              <Grid xs={12} item>
                <Controller
                  name="marketArchetypeId"
                  control={control}
                  render={({ field }) => (
                    <MarketLevelBlueprintSearch
                      value={field.value}
                      onChange={field.onChange}
                      label={t('live-events:market_level_blueprint')}
                      showDiscontinuedToggle={true}
                    />
                  )}
                />
              </Grid>

              {mismatchedTypeNames.length > 0 && (
                <Grid xs={12} item>
                  <Alert severity="warning">
                    {t('internal-live-events:blueprint_market_archetype_mismatch_warning', { types: mismatchedTypeNames.join(', ') })}
                  </Alert>
                </Grid>
              )}

              <Grid xs={2} item sx={{ display: 'flex', justifyContent: 'center' }}>
                <Controller
                  name="discontinued"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                      label={t('live-events:live_event_evolution_type_discontinued')}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <CardActions sx={{ justifyContent: 'flex-end', padding: 1 }}>
            {!archetypeId ? (
              <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleAddArchetype} disabled={!isEnglishNameValid || isCreating}>
                {isCreating ? t('internal-live-events:adding') : t('internal-live-events:add')}
              </Button>
            ) : (
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" color="primary" startIcon={<Cancel />} onClick={handleCancel} disabled={isUpdating}>
                  {t('internal-common:cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Save />}
                  onClick={handleSaveArchetype}
                  disabled={!isEnglishNameValid || isUpdating || waitingForMismatchData}
                >
                  {isUpdating ? t('internal-live-events:saving') : t('internal-common:save')}
                </Button>
              </Stack>
            )}
          </CardActions>
        </Card>
      </FormProvider>

      <Card sx={{ marginTop: 3 }}>
        <CardHeader title={t('internal-live-events:archetypes_for_this_game')} />
        <Divider />
        <CardContent sx={{ padding: 0, '&:last-child': { paddingBottom: 0 } }}>
          <TableContainer component={Box}>
            <GRTable
              rows={archetypes}
              columns={columns}
              rowIdKey="archetypeId"
              isLoading={isLoading}
              gridlines={true}
              striped
              hoverable
              onColumnsUpdated={handleColumnsUpdate}
              noRowsLabel={t('internal-live-events:no_archetypes_found')}
              customProps={customProps}
            />
          </TableContainer>
        </CardContent>
      </Card>

      <FloatingToolbar>
        <BackButton mb={0} url={`/internal/live-events-tracking/events/${trackedGameId}`} />
      </FloatingToolbar>

      <ConfirmDialog open={confirmDialogOpen} onClose={handleConfirmDialogClose} confirmDialogData={confirmDialogData} />
    </Box>
  )
}
